// Ported from eb-ui `src/legacy/Collection/Collection.tsx` — the main
// collection render (hero + details + events + share + organizer panel + the
// two "related" carousels).
//   - `@eventbrite/legacy-infra.context-gen` `CoreEnv` → the local `Env`
//     (`../types`).
//   - `@eventbrite/eds-layout` → `../lib/eds/Layout`.
//   - `@eventbrite/legacy-infra.i18n` → `@/lib/gettext`.
//   - `@eventbrite/social-share` `ShareCollectionProps` → the vendored shared
//     module (same contract).
import { ShareCollectionProps } from '@eventbrite/social-share';
import React from 'react';

import { CardsCarousel, CardsType } from '../CardsCarousel/CardsCarousel';
import { CollectionEvents } from '../CollectionEvents';
import { CollectionHero } from '../components/CollectionHero/CollectionHero';
import {
    OrganizerPanel,
    OrganizerPanelProps,
} from '../components/OrganizerPanel/OrganizerPanel';
import {
    ShareCollectionInline,
    ShareCollectionModal,
} from '../components/Share/ShareCollection';
import {
    AffiliateCodes,
    CollectionCardOriginType,
    MoreCollectionsFromThisOrganizer,
    OtherEventsYouMayLike,
} from '@/types';
import {
    getMoreCollectionsFromThisOrganizerTitle,
    getOtherEventsYouMayLikeTitle,
} from '../_shared/constants';
import { Layout } from '../lib/eds/Layout';
import type { Env } from '../types';
import { gettext } from '@/lib/gettext';
import { CollectionDetails, CollectionDetailsProps } from './';

interface Props {
    collectionDetailsProps: CollectionDetailsProps;
    organizerPanelProps: OrganizerPanelProps;
    collectionShareProps: ShareCollectionProps;
    moreCollectionsFromThisOrganizerProps?: MoreCollectionsFromThisOrganizer;
    otherEventsYouMayLikeProps?: OtherEventsYouMayLike;
    env: Env;
}

const filterCollectionCardOwner = (
    collections: CollectionCardOriginType[] | undefined,
    collectionId: string,
) => collections?.filter((collection) => collection.id !== collectionId);

export const Collection: React.FC<Props> = ({
    collectionDetailsProps,
    organizerPanelProps,
    collectionShareProps,
    moreCollectionsFromThisOrganizerProps,
    otherEventsYouMayLikeProps,
    env,
}) => {
    const modalContainer: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef(null);
    const moreCollectionFromThisOrganizer = filterCollectionCardOwner(
        moreCollectionsFromThisOrganizerProps?.event_groups,
        collectionShareProps.collectionId,
    );
    const otherEventsYouMayLike = otherEventsYouMayLikeProps?.events;

    return (
        <>
            <CollectionHero>
                <ShareCollectionModal
                    container={modalContainer}
                    {...collectionShareProps}
                />
            </CollectionHero>

            <Layout maxWidth="large" hasHorizontalGutters={false}>
                <div className="cc-main-wrapper">
                    <CollectionDetails {...collectionDetailsProps} />

                    <CollectionEvents env={env} />

                    <div className="eds-l-mar-bot-10">
                        <h2 className="eds-text-hm eds-l-mar-bot-4 cc-heading">
                            {gettext('Share')}
                        </h2>
                        <ShareCollectionInline {...collectionShareProps} />
                    </div>

                    {/* We Should replace the next code by the <Hr /> EDS component */}
                    <hr
                        className="eds-divider__hr eds-bg-color--grey-200 eds-divider--horizontal"
                        aria-hidden="true"
                    />
                    <OrganizerPanel {...organizerPanelProps} />
                </div>
            </Layout>

            {moreCollectionFromThisOrganizer?.length ? (
                <Layout maxWidth="large" hasHorizontalGutters={true}>
                    <div className="cc-outer-wrapper">
                        <CardsCarousel
                            cards={moreCollectionFromThisOrganizer}
                            cardsType={CardsType.COLLECTION}
                            title={getMoreCollectionsFromThisOrganizerTitle()}
                            newTitleStyle={true}
                            affCode={
                                AffiliateCodes.RelatedCollectionsFromOrganizer
                            }
                            dataTestId="mcfto-carousel"
                        />
                    </div>
                </Layout>
            ) : null}

            {otherEventsYouMayLike?.length ? (
                <Layout maxWidth="large" hasHorizontalGutters={true}>
                    <div className="cc-outer-wrapper">
                        <CardsCarousel
                            cards={otherEventsYouMayLike}
                            cardsType={CardsType.EVENT}
                            title={getOtherEventsYouMayLikeTitle()}
                            newTitleStyle={true}
                            affCode={
                                AffiliateCodes.RelatedEventsOtherOrganizers
                            }
                            isFromDestination={true}
                            dataTestId="oeyml-carousel"
                        />
                    </div>
                </Layout>
            ) : null}

            <div id="share-modal-wrapper" ref={modalContainer}></div>
        </>
    );
};
